import styles from '../../RiskIndicators.module.scss';
import { useRiskIndicatorStates } from '../../useRiskIndicatorStates';
import { EMPTY_NAME_PLACEHOLDER, TYPES } from '../../RiskIndicatorConstants';
import { Row } from '../../RiskIndicators';
import { Header } from './RiskIndicatorHeaders';
import { normalizeNameKey } from '../../../../utils/utils';
import { getRiskCellStyle } from '../../riskIndicatorUtils';
import { useAppDispatch } from '../../../../app/hooks';
import { removeTooltipContent, setTooltipContent } from '../../../../store/tooltip/tooltip_actions';
import { formatDesignationWithShareholding } from '../../../../utils/formatShareholdingPercent';
import DirectorHoverCardTooltip from '../../../Dashboard/DashboardModules/RelatedParties/components/DirectorHoverCard/DirectorHoverCardTooltip';
import type { DirectorHoverProfile } from '../../../Dashboard/DashboardModules/RelatedParties/components/DirectorHoverCard/DirectorHoverCard.types';
import { DirectorStatusText, resolveDirectorStatus } from '../../../../common/DirectorStatus/DirectorStatus';

interface RiskIndicatorRowsProps {
    rows: Row[];
    setRows: React.Dispatch<React.SetStateAction<Row[]>>;
    headers: Header[];
    isPDFReport?: boolean;
    onOpenRegulatoryRisk?: (row: Row, checkType: string) => void;
}

const RiskIndicatorRows = ({ rows, setRows, headers, isPDFReport = false, onOpenRegulatoryRisk }: RiskIndicatorRowsProps) => {
    const { getRowImage, getRiskIndicator } = useRiskIndicatorStates({
        rows,
        setRows,
        autoFetch: false,
    });
    const dispatch = useAppDispatch();

    return (
        <>
            {rows?.map((row, index) => {
                // Company rows never show a designation, so they never show shareholding either —
                // matching Related Party/Credit Bureau, which skip it for non-person rows.
                const designationAndShareholding =
                    row?.type === TYPES.INDIVIDUAL
                        ? formatDesignationWithShareholding(row?.designation, row?.shareholdingPercentage, { hideWhenNull: true })
                        : '';
                // polus attaches this per person row (injectAmlPepDesignation, RECUR-27924). When it
                // is present the rich hover card replaces the plain truncation tooltip — showing both
                // would stack two tooltips on the same element.
                const directorProfile: DirectorHoverProfile | undefined = row?.directorProfile;
                // Same Active/Former decision Management Profile shows, rendered as the
                // last segment of the designation/shareholding sub-line (not beside the name) —
                // hidden when the director has no isActive. Not gated on row.type: directorProfile is
                // only ever attached to person rows (see the comment above), so it is already empty
                // for a company row — matching how the pill was resolved before this rework.
                const directorStatus = resolveDirectorStatus(directorProfile?.isActive);
                // A person with no roster match still gets a card, saying so — see DirectorHoverCardEmpty.
                const unmatched =
                    !directorProfile && row?.type === TYPES.INDIVIDUAL
                        ? {
                              name: row?.name,
                              designation: row?.designation,
                              shareholdingPercentage: row?.shareholdingPercentage,
                          }
                        : undefined;
                const showHoverCard = !isPDFReport && !!(directorProfile || unmatched);
                const nameStack = (
                    <span
                        // Two-line cell per Figma: name on top, "<designation> • <shareholding>%"
                        // as a lighter subline beneath it. Both lines truncate, so hovering
                        // reveals the full text via the shared tooltip (matches Legal Cases).
                        className={styles.NameStack}
                        onMouseEnter={
                            showHoverCard
                                ? undefined
                                : (e) => {
                                      if (isPDFReport) return;
                                      const detail = designationAndShareholding ? ` • ${designationAndShareholding}` : '';
                                      dispatch(
                                          setTooltipContent({
                                              content: `${row?.name ?? ''}${detail}`,
                                              target: e.currentTarget,
                                              placement: 'top',
                                          }),
                                      );
                                  }
                        }
                        onMouseLeave={showHoverCard ? undefined : () => removeTooltipContent(dispatch)}
                    >
                        <span className={styles.NameText}>{row?.name?.trim() || EMPTY_NAME_PLACEHOLDER}</span>
                        {(designationAndShareholding || directorStatus) && (
                            <span className={styles.DesignationText}>
                                {designationAndShareholding}
                                {directorStatus && (
                                    <>
                                        {designationAndShareholding && ' • '}
                                        <DirectorStatusText status={directorStatus} />
                                    </>
                                )}
                            </span>
                        )}
                    </span>
                );
                return (
                    <div
                        className={styles.TableRow}
                        key={index}
                        data-ri-director={normalizeNameKey(row?.name)}
                        data-ri-din={row?.identifier ?? ''}
                    >
                        <div className={styles.StickyRows}>
                            <div className={styles.Name}>
                                <span className={styles.RowIcon}>{getRowImage(row?.type)}</span>
                                {showHoverCard ? (
                                    <DirectorHoverCardTooltip profile={directorProfile} unmatched={unmatched}>
                                        {nameStack}
                                    </DirectorHoverCardTooltip>
                                ) : (
                                    nameStack
                                )}
                            </div>
                        </div>
                        {headers.map((header, headerIndex) => (
                            <div
                                className={styles.TableRowItem}
                                key={headerIndex}
                                // Matches RiskIndicatorHeaders' data-column-key — see
                                // .TableHeadRowItem[data-column-key] in RiskIndicators.module.scss.
                                data-column-key={header.key}
                                style={getRiskCellStyle(row, header.key)}
                            >
                                {getRiskIndicator(row?.riskProfile?.[header.key], row, header.key, onOpenRegulatoryRisk)}
                            </div>
                        ))}
                    </div>
                );
            })}
        </>
    );
};

export default RiskIndicatorRows;
